/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { or, sort, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { camelize, dasherize } from '@ember/string';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import Judgement from '@linc-technologies/linced-common/enums/progression-judgement';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import type SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import SubjectTypesEnum from '@linc-technologies/linced-common/enums/subject-type';
import type GoalCategory from '@linc-technologies/linced-common/models/goals/category';
import type Milestone from '@linc-technologies/linced-common/models/milestones/milestone';
import type MilestoneSet from '@linc-technologies/linced-common/models/milestones/milestone-set';
import type School from '@linc-technologies/linced-common/models/school';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import type StringsService from '@linc-technologies/linced-common/services/strings';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// The progress terms used when a school hasn't set its own
// (progress:strings:* school options), matching the strings services.
const DEFAULT_PROGRESS_STRINGS: Readonly<Record<number, string>> =
  Object.freeze({
    1: 'well below',
    2: 'below',
    3: 'at',
    4: 'above',
    5: 'well above',
  });

// Proto: goals.GoalSubject.
export default class Subject extends Model {
  declare [Type]: 'goals/subject';

  @service
  declare options: OptionsService;

  @service
  declare strings: StringsService;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @alias('school.id')
  declare school_id: string | null | undefined;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @hasMany('goals/category', { async: true, inverse: 'subjectId' })
  declare categories: AsyncHasMany<GoalCategory>;

  @computed('categories.content.[]')
  get categoriesArray() {
    return resolved(this.categories);
  }

  categorySortOrder = Object.freeze(['sequence', 'label']);

  @sort('categoriesArray', 'categorySortOrder')
  declare sortedCategories: GoalCategory[];

  @belongsTo('milestones/milestone-set', { async: true, inverse: null })
  declare milestoneSetId: AsyncBelongsTo<MilestoneSet>;

  @alias('milestoneSetId')
  declare milestoneSet: AsyncBelongsTo<MilestoneSet>;

  @alias('milestoneSet.id')
  declare milestone_set_id: string | null | undefined;

  @computed('milestoneSet.{id,milestones.[]}')
  get milestones() {
    const milestoneSetId = this.get('milestoneSet.id');
    const allMilestones = this.store.peekAll<Milestone>('milestones/milestone');
    if (!isEmpty(milestoneSetId)) {
      // This Subject has a Milestone Set selected.  Use its Milestones.
      return allMilestones
        .filter((milestone) => milestone.get('set_id') === milestoneSetId)
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    } else {
      // Use the school's default Milestones (those not in a Milestone Set).
      return allMilestones
        .filter((milestone) => {
          if (milestone.school_id !== this.school_id) {
            // Exclude any Milestones which don't belong to this school.
            return false;
          }
          if (!isEmpty(milestone.set_id)) {
            // Exclude any Milestones which are part of a Set.
            return false;
          }
          // Only include any Milestones with progress type "Progression".
          return milestone.progressionType === ProgressionType.PROGRESSION;
        })
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    }
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('number', { defaultValue: 0 })
  declare subjectType: EnumValue<typeof SubjectTypesEnum>;

  @computed('subjectType')
  get subjectTypeName() {
    const subjectType = this.subjectType;
    // return the subjectType return the Subject Type Label
    return subjectType ? SubjectTypesEnum.getLabel(subjectType) : 'Goals';
  }

  @computed('subjectType')
  get subjectTypePriority() {
    const obj = SubjectTypesEnum.getObject(this.subjectType);
    return (obj && obj.priority) || 4;
  }

  @attr('string')
  declare subjectCategory: string | undefined;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('boolean', { defaultValue: false })
  declare trackable: boolean;

  @attr('boolean', { defaultValue: false })
  declare calculateBestFits: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToTeachers: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToParents: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToLearners: boolean;

  @or('visibleToTeachers', 'visibleToParents', 'visibleToLearners')
  declare isVisible: boolean;

  @attr('number', { defaultValue: 0 })
  declare reportVisibility: EnumValue<typeof SubjectReportVisibility> | 0;

  @attr('boolean', { defaultValue: false })
  declare showRadialDials: boolean;

  @attr('boolean', { defaultValue: false })
  declare isGradable: boolean;

  @attr('boolean', { defaultValue: false })
  declare isTopic: boolean;

  @attr('string', { defaultValue: '' })
  declare SC1: string;

  @attr('string', { defaultValue: '' })
  declare SC2: string;

  @attr('string', { defaultValue: '' })
  declare SC3: string;

  @attr('string', { defaultValue: '' })
  declare SC4: string;

  @attr('string', { defaultValue: '' })
  declare SC5: string;

  // onTrackInterimGrade / onTrackFullGrade are the minimum Progression
  // judgements required to count as on track for an interim
  // (Milestone.isInterim) and a full Milestone respectively. The API's Grade
  // enum aligns exactly with progression-judgement, so these compare directly
  // against a Progression's judgement.
  @attr('number', { defaultValue: 0 })
  declare onTrackInterimGrade: number;

  @attr('number', { defaultValue: 0 })
  declare onTrackFullGrade: number;

  // defaultGrades provides an array of grade metadata objects, using the
  // school's default progress judgement terminology, or the built-in terms
  // where the school hasn't set its own.
  @computed('school', 'school.fields')
  get defaultGrades() {
    const ordinals = ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth'];
    return [1, 2, 3, 4, 5].map((grade) => {
      // Grades 1 to 5 are all in the enum, so the label is not null.
      const judgementLabel = Judgement.getLabel(grade) as string;
      return {
        value: grade,
        colourClass: `circle--${dasherize(judgementLabel)}`,
        // schoolGet gives unknown: the options service does not type the values.
        colour: this.options.schoolGet(
          this.school,
          `progress:colours:${ordinals[grade]}`,
        ) as string | undefined,
        string:
          (this.options.schoolGet(
            this.school,
            `progress:strings:${camelize(judgementLabel)}`,
          ) as string | undefined) || DEFAULT_PROGRESS_STRINGS[grade],
      };
    });
  }

  // possibleGrades provides an array of grade metadata objects, using any
  // subject-specific success criteria, falling back to the school's default
  // progress judgement terminology.
  @computed('SC1', 'SC2', 'SC3', 'SC4', 'SC5', 'defaultGrades.[]')
  get possibleGrades() {
    const grades: Subject['defaultGrades'] = [];
    if (isEmpty(this.defaultGrades)) {
      return grades;
    }

    const hasAnySuccessCriteria = this.defaultGrades.some((grade) => {
      const label = (this.get(`SC${grade.value}`) as string | undefined) ?? '';
      return !isEmpty(label.trim());
    });
    if (!hasAnySuccessCriteria) {
      // No custom labels; just use the (full set of) defaults.
      return this.defaultGrades;
    }

    // At least one custom label is set.  Use only non-empty labels.
    this.defaultGrades.forEach((grade) => {
      const label = (this.get(`SC${grade.value}`) as string | undefined) ?? '';
      if (!isEmpty(label.trim())) {
        // Make a copy to ensure that we don't update the `string` property
        // twice in one runloop.
        grades.push({
          value: grade.value,
          colourClass: grade.colourClass,
          colour: grade.colour,
          string: label,
        });
      }
    });
    return grades;
  }

  // judgementGrades lists all five progress judgement levels, lowest first,
  // each named by the Subject's success criteria where set and by the
  // school's (or the built-in) terminology otherwise. Reporting and insights
  // judge against all five levels, unlike possibleGrades, which narrows to
  // the levels a Subject names for goal grading. Levels named by the Subject
  // are marked isSubjectName, so they can be shown exactly as entered.
  @computed('SC1', 'SC2', 'SC3', 'SC4', 'SC5', 'defaultGrades.[]')
  get judgementGrades() {
    return this.defaultGrades.map((grade) => {
      const label = (
        (this.get(`SC${grade.value}`) as string | undefined) ?? ''
      ).trim();
      return label ? { ...grade, string: label, isSubjectName: true } : grade;
    });
  }
}
